import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { projectHelper, userHelper } from '../common/src/helpers'
import { useMe } from '../common/src/hooks/usersHooks'

interface ConfirmationModalState {
  open: boolean
  title: string
  subText: string
  primaryActionText: string
  onPrimaryActionClick?: () => void
}

interface RestrictedProjectConfirmationOptions {
  project: any
  action: 'add_member' | 'change_owner' | 'add_follower' | 'add_group'
  email?: string
  groupName?: string
  memberCount?: number
}

/**
 * Hook to handle confirmation modals for restricted project actions
 * Shows warnings when adding external users to restricted projects
 */
export const useRestrictedProjectConfirmation = () => {
  const { t } = useTranslation()
  const me = useMe()
  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    open: false,
    title: '',
    subText: '',
    primaryActionText: '',
  })

  const isExternalUser = useCallback(
    (email: string, project: any) => {
      if (!email || !project) return false
      
      // Check if user is already a project member
      const membership = projectHelper.membershipByUserEmail(project, email)
      if (membership) return false
      
      // Check if user is the current user
      const currentUserEmail = userHelper.getEmail(me)
      if (email === currentUserEmail) return false
      
      return true
    },
    [me]
  )

  const shouldShowConfirmation = useCallback(
    (project: any, email?: string) => {
      if (!project) return false
      
      // Only show confirmation for restricted projects
      if (!projectHelper.isRestricted(project)) return false
      
      // Only show confirmation for external users
      if (email && !isExternalUser(email, project)) return false
      
      return true
    },
    [isExternalUser]
  )

  const showConfirmation = useCallback(
    (options: RestrictedProjectConfirmationOptions, onConfirm: () => void) => {
      const { project, action, email, groupName, memberCount } = options
      
      // If no confirmation needed, execute immediately
      if (!shouldShowConfirmation(project, email)) {
        onConfirm()
        return
      }

      let title: string
      let subText: string
      let primaryActionText: string

      switch (action) {
        case 'add_member':
          title = t('project.restricted_projects.confirmation.add_member_title')
          subText = t('project.restricted_projects.confirmation.add_member_message', { email })
          primaryActionText = t('project.restricted_projects.confirmation.add_member_proceed')
          break
        case 'change_owner':
          title = t('project.restricted_projects.confirmation.change_owner_title')
          subText = t('project.restricted_projects.confirmation.change_owner_message', { email })
          primaryActionText = t('project.restricted_projects.confirmation.change_owner_proceed')
          break
        case 'add_follower':
          title = t('project.restricted_projects.confirmation.add_follower_title')
          subText = t('project.restricted_projects.confirmation.add_follower_message', { email })
          primaryActionText = t('project.restricted_projects.confirmation.add_follower_proceed')
          break
        case 'add_group':
          title = t('project.restricted_projects.confirmation.add_group_title')
          subText = t('project.restricted_projects.confirmation.add_group_message', { 
            groupName,
            memberCount 
          })
          primaryActionText = t('project.restricted_projects.confirmation.add_group_proceed')
          break
        default:
          // Fallback - shouldn't reach here
          onConfirm()
          return
      }

      setConfirmationModalState({
        open: true,
        title,
        subText,
        primaryActionText,
        onPrimaryActionClick: () => {
          setConfirmationModalState(prev => ({ ...prev, open: false }))
          onConfirm()
        },
      })
    },
    [t, shouldShowConfirmation]
  )

  const dismissConfirmation = useCallback(() => {
    setConfirmationModalState(prev => ({ ...prev, open: false }))
  }, [])

  return {
    confirmationModalState,
    showConfirmation,
    dismissConfirmation,
    shouldShowConfirmation,
  }
}